import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, CheckCircle2, Home, ArrowRight, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import WizardLayout from '@/components/wizard/WizardLayout';
import { getActiveRegionConfig } from '@/lib/regionConfig';
import { toast } from 'sonner';

const EXPERT_SUBMISSION_STORAGE_KEY = 'ixbrl_expert_submission';

const WizardExpert = () => {
  const navigate = useNavigate();
  const regionConfig = getActiveRegionConfig();
  const [jobDetails, setJobDetails] = useState(null);

  useEffect(() => {
    const wizardData = JSON.parse(sessionStorage.getItem(EXPERT_SUBMISSION_STORAGE_KEY) || '{}');
    if (!wizardData.jobId) {
      toast.error('Session expired');
      navigate('/wizard/start');
      return;
    }

    toast.success('Expert help request submitted');
    setJobDetails(wizardData);
  }, [navigate]);

  if (!jobDetails) return null;

  const primaryCompany = jobDetails.company || { name: jobDetails.jobs?.[0]?.companyName || 'Your company' };
  const deliveryLabel = jobDetails.deliveryTime === 'express' ? 'Express 8-hour turnaround' : 'Standard 24-hour turnaround';

  return (
    <WizardLayout currentStep={4} title="Expert Request Submitted" company={primaryCompany}>
      <div className="max-w-3xl mx-auto text-center">
        <div className="mb-8">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">Request Submitted Successfully</h2>
          <p className="text-muted-foreground">
            Your order is now live in the Provider Portal queue, and our expert team can start working on it straight away.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 mb-8 text-left">
          <div className="bg-muted border border-border rounded-lg p-6">
            <h3 className="font-medium mb-4">Order Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Primary Job</span>
                <span className="font-mono">{jobDetails.jobId}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Companies</span>
                <span>{jobDetails.jobs?.length || 1}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Payment</span>
                <span>{jobDetails.paymentType === 'pay_now' ? 'Paid online' : 'Invoice later'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Delivery</span>
                <span>{deliveryLabel}</span>
              </div>
            </div>
          </div>

          <div className="bg-muted border border-border rounded-lg p-6">
            <h3 className="font-medium mb-4">Jobs Created</h3>
            <div className="space-y-3 text-sm">
              {(jobDetails.jobs || []).map((job) => (
                <div key={job.jobId} className="flex items-start justify-between gap-4 rounded-lg bg-background/70 px-3 py-2 border border-border">
                  <div>
                    <p className="font-medium">{job.companyName || 'Company'}</p>
                    <p className="text-muted-foreground">Queued for expert preparation</p>
                  </div>
                  <span className="font-mono text-xs">{job.jobId}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="border-l-4 border-blue-600 bg-blue-50 dark:bg-blue-950/20 p-5 mb-8 text-left rounded">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-700 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">What happens next?</p>
              <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-2">
                <li>Our team sees these jobs immediately in the Provider Portal queue.</li>
                <li>You can track the same live workflow from your Jobs page without waiting for a second sync.</li>
                <li>When work reaches review stage, your Client Portal will show the matching review actions.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 mb-8 text-left">
          <h3 className="font-medium mb-4">Contact & Tracking</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Support</span>
              <a href={`mailto:${regionConfig.supportEmail}`} className="inline-flex items-center gap-2 text-primary hover:underline">
                <Mail className="w-4 h-4" />
                {regionConfig.supportEmail}
              </a>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Best place to track progress</span>
              <span>Your Jobs dashboard</span>
            </div>
          </div>
        </div>

        <div className="flex gap-4 justify-center">
          <Button variant="outline" onClick={() => { sessionStorage.removeItem(EXPERT_SUBMISSION_STORAGE_KEY); navigate('/'); }}>
            <Home className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <Button onClick={() => { sessionStorage.removeItem(EXPERT_SUBMISSION_STORAGE_KEY); navigate('/jobs'); }}>
            View Jobs
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </WizardLayout>
  );
};

export default WizardExpert;
